import { createContext } from 'react';
import type { User } from 'oidc-client-ts';

export const REQUIRED_ROLE = 'tgo-admin';

export interface AuthState {
  readonly user: User | null;
  readonly isLoading: boolean;
  readonly isLoggingOut: boolean;
  readonly isAuthenticated: boolean;
  readonly hasRequiredRole: boolean;
  readonly error: Error | null;
}

export interface AuthContextValue extends AuthState {
  readonly login: (returnUrl?: string) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly getAccessToken: () => Promise<string | null>;
  readonly handleSigninCallback: () => Promise<User | undefined>;
}

/**
 * Extracts roles from the user's tokens.
 * Checks both the ID token profile and decodes the access token for roles.
 * Keycloak typically includes roles in resource_access.<client>.roles
 */
export function getUserRoles(user: User | null): readonly string[] {
  if (!user) return [];
  
  const allRoles: string[] = [];
  
  // Check ID token profile for roles
  if (user.profile) {
    const profile = user.profile as Record<string, unknown>;
    
    // Check for 'roles' claim
    if (Array.isArray(profile.roles)) {
      allRoles.push(...(profile.roles as string[]));
    }
    
    // Check for realm_access.roles
    const realmAccess = profile.realm_access as { roles?: string[] } | undefined;
    if (realmAccess?.roles && Array.isArray(realmAccess.roles)) {
      allRoles.push(...realmAccess.roles);
    }
    
    // Check for resource_access (client-specific roles) in profile
    const profileResourceAccess = profile.resource_access as Record<string, { roles?: string[] }> | undefined;
    if (profileResourceAccess) {
      for (const resource of Object.values(profileResourceAccess)) {
        if (resource.roles && Array.isArray(resource.roles)) {
          allRoles.push(...resource.roles);
        }
      }
    }
  }
  
  // Decode access token to get roles (Keycloak puts roles in access token)
  if (user.access_token) {
    try {
      const tokenParts = user.access_token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1])) as Record<string, unknown>;
        
        // Check realm_access in access token
        const realmAccess = payload.realm_access as { roles?: string[] } | undefined;
        if (realmAccess?.roles && Array.isArray(realmAccess.roles)) {
          allRoles.push(...realmAccess.roles);
        }
        
        // Check resource_access in access token
        const resourceAccess = payload.resource_access as Record<string, { roles?: string[] }> | undefined;
        if (resourceAccess) {
          for (const resource of Object.values(resourceAccess)) {
            if (resource.roles && Array.isArray(resource.roles)) {
              allRoles.push(...resource.roles);
            }
          }
        }
      }
    } catch {
      // Failed to decode token, continue with what we have
    }
  }
  
  // Return unique roles
  return [...new Set(allRoles)];
}

export function hasRole(user: User | null, role: string): boolean {
  const roles = getUserRoles(user);
  return roles.includes(role);
}

export const AuthContext = createContext<AuthContextValue | null>(null);
